import { useState } from "react";
import type { FormEvent } from "react";

const SUBMIT_URL = "https://readdy.ai/api/form/daind4joh653ivfvoh00";

const contactPoints = [
  {
    icon: "ri-mail-line",
    label: "联系邮箱",
    value: "hello@51chigua.com",
    href: "mailto:hello@51chigua.com",
  },
  {
    icon: "ri-chat-forward-line",
    label: "爆料线索",
    value: "填写右侧表单，我们会优先排期",
    href: "#tip-form",
  },
  {
    icon: "ri-time-line",
    label: "处理节奏",
    value: "工作日 48 小时内查看线索",
    href: "",
  },
];

export default function ContactSection() {
  const [status, setStatus] = useState<"idle" | "submitting" | "success" | "error">("idle");
  const [message, setMessage] = useState("");

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const payload = new FormData(form);

    // anti-spam honeypot: bots tend to fill every field
    const trap = String(payload.get("website_alt") || "").trim();
    if (trap.length > 0) {
      setStatus("success");
      setMessage("线索已提交，感谢你的分享，我们会尽快查看。");
      form.reset();
      return;
    }

    const body = new URLSearchParams();
    payload.forEach((value, key) => {
      if (key === "website_alt") return;
      const text = String(value).trim();
      if (text.length > 0) body.append(key, text);
    });

    setStatus("submitting");
    setMessage("");

    try {
      const response = await fetch(SUBMIT_URL, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: body.toString(),
      });

      const responseText = await response.text();
      let parsed: { code?: string; message?: string; meta?: { message?: string; detail?: string } } | null =
        null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      const serverMsg =
        parsed?.meta?.message ||
        parsed?.meta?.detail ||
        parsed?.message ||
        responseText ||
        "";
      const isSpam = typeof serverMsg === "string" && serverMsg.toLowerCase().includes("spam");
      const isOk = response.ok && parsed?.code === "OK" && !isSpam;

      if (isOk) {
        setStatus("success");
        setMessage("线索已提交，感谢你的分享，我们会尽快查看并纳入盘点排期。");
        form.reset();
      } else {
        setStatus("error");
        setMessage(
          serverMsg && serverMsg.length > 0
            ? serverMsg
            : "提交失败，请检查填写内容后重试，或直接发送邮件给我们。"
        );
      }
    } catch {
      setStatus("error");
      setMessage("网络异常，提交未成功。请稍后重试，或直接发送邮件给我们。");
    }
  };

  return (
    <section id="contact" className="w-full bg-background-50 px-4 md:px-8 py-14 md:py-20">
      <div className="w-full grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-14">
        <div>
          <span className="inline-flex items-center gap-1.5 text-xs md:text-sm font-medium tracking-widest text-primary-600 mb-3">
            <i className="ri-subtract-line" />
            联系与爆料
          </span>
          <h2 className="font-heading font-bold text-foreground-950 text-2xl md:text-3xl lg:text-4xl leading-snug mb-4">
            有好瓜，欢迎直接丢给我们
          </h2>
          <p className="text-sm md:text-base text-foreground-600 leading-relaxed mb-8">
            你在别处看到一条线索，但不确定值不值得跟？把它交给我们。留下关键词与大致时间，编辑会先做一轮交叉验证，再决定是否纳入盘点排期。
          </p>

          <ul className="space-y-4">
            {contactPoints.map((point) => (
              <li
                key={point.label}
                className="flex items-center gap-4 rounded-lg border border-background-200 bg-background-100/60 p-4"
              >
                <span className="w-11 h-11 shrink-0 flex items-center justify-center rounded-md bg-primary-100 text-primary-700 text-xl">
                  <i className={point.icon} />
                </span>
                <span className="min-w-0">
                  <span className="block text-xs text-foreground-500 mb-0.5">{point.label}</span>
                  {point.href ? (
                    <a
                      href={point.href}
                      className="block text-sm font-medium text-foreground-900 truncate hover:text-primary-600 transition-colors cursor-pointer"
                    >
                      {point.value}
                    </a>
                  ) : (
                    <span className="block text-sm font-medium text-foreground-900">{point.value}</span>
                  )}
                </span>
              </li>
            ))}
          </ul>
        </div>

        <div className="rounded-lg border border-background-200 bg-background-100/60 p-5 md:p-7">
          <h3 className="font-heading font-bold text-foreground-950 text-lg md:text-xl mb-1">
            爆料线索提交
          </h3>
          <p className="text-xs md:text-sm text-foreground-500 mb-6">
            带 <span className="text-primary-600">*</span> 的为必填项，线索描述请控制在 500 字以内。
          </p>

          <form
            id="tip-form"
            data-readdy-form
            onSubmit={handleSubmit}
            className="space-y-4"
            noValidate
          >
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label htmlFor="tip-name" className="block text-sm text-foreground-800 mb-1.5">
                  称呼 <span className="text-primary-600">*</span>
                </label>
                <input
                  id="tip-name"
                  name="name"
                  type="text"
                  required
                  autoComplete="name"
                  placeholder="怎么称呼你"
                  className="w-full px-3.5 py-2.5 rounded-md border border-background-300 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-200 transition-colors"
                />
              </div>
              <div>
                <label htmlFor="tip-email" className="block text-sm text-foreground-800 mb-1.5">
                  邮箱 <span className="text-primary-600">*</span>
                </label>
                <input
                  id="tip-email"
                  name="email"
                  type="email"
                  required
                  autoComplete="email"
                  placeholder="用于回复你的线索"
                  className="w-full px-3.5 py-2.5 rounded-md border border-background-300 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-200 transition-colors"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label htmlFor="tip-category" className="block text-sm text-foreground-800 mb-1.5">
                  线索分类
                </label>
                <select
                  id="tip-category"
                  name="category"
                  defaultValue="影视剧集"
                  className="w-full px-3.5 py-2.5 rounded-md border border-background-300 bg-background-50 text-sm text-foreground-900 outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-200 transition-colors cursor-pointer"
                >
                  <option value="影视剧集">影视剧集</option>
                  <option value="综艺前线">综艺前线</option>
                  <option value="音乐现场">音乐现场</option>
                  <option value="品牌联名">品牌联名</option>
                  <option value="明星动态">明星动态</option>
                  <option value="短剧出海">短剧出海</option>
                </select>
              </div>
              <div>
                <label htmlFor="tip-topic" className="block text-sm text-foreground-800 mb-1.5">
                  事件关键词
                </label>
                <input
                  id="tip-topic"
                  name="topic"
                  type="text"
                  placeholder="例如：某剧番位 / 音乐节抢票"
                  className="w-full px-3.5 py-2.5 rounded-md border border-background-300 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-200 transition-colors"
                />
              </div>
            </div>

            <div>
              <label htmlFor="tip-detail" className="block text-sm text-foreground-800 mb-1.5">
                线索描述 <span className="text-primary-600">*</span>
              </label>
              <textarea
                id="tip-detail"
                name="detail"
                required
                rows={5}
                maxLength={500}
                placeholder="简单说说事件的大致时间、涉及方和你知道的线索来源（不超过 500 字）"
                className="w-full px-3.5 py-2.5 rounded-md border border-background-300 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-200 transition-colors resize-none"
              />
            </div>

            <div className="alt-contact-field" aria-hidden="true">
              <label htmlFor="tip-website-alt">请勿填写此栏</label>
              <input
                id="tip-website-alt"
                name="website_alt"
                type="text"
                tabIndex={-1}
                autoComplete="off"
                readOnly
              />
            </div>

            <button
              type="submit"
              disabled={status === "submitting"}
              className="w-full inline-flex items-center justify-center gap-2 px-6 py-3 rounded-md bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {status === "submitting" ? (
                <>
                  <i className="ri-loader-4-line animate-spin" />
                  正在提交
                </>
              ) : (
                <>
                  <i className="ri-send-plane-line" />
                  提交线索
                </>
              )}
            </button>

            {status === "success" && message && (
              <p className="flex items-start gap-2 text-sm text-secondary-700 bg-secondary-50 border border-secondary-200 rounded-md px-3.5 py-3">
                <i className="ri-checkbox-circle-fill mt-0.5" />
                {message}
              </p>
            )}
            {status === "error" && message && (
              <p className="flex items-start gap-2 text-sm text-primary-700 bg-primary-50 border border-primary-200 rounded-md px-3.5 py-3">
                <i className="ri-error-warning-fill mt-0.5" />
                {message}
              </p>
            )}
          </form>
        </div>
      </div>
    </section>
  );
}